import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AdminCreateVipPartnerPackInput,
  AdminUserCrmManagerPartnerShareBody,
  AdminUserDto,
  AdminUserInvestorBody,
  AdminUserUpdateRolesBody,
  AdminUserVipBody,
  AdminVipPartnerPackDto,
  FakeUserCreateInput,
  FakeUserDto,
  FakeUserUpdateInput,
} from '@upme/contracts';
import { api } from '../../app/api-client';
import { fakeByIdOptions, fakesKey, userByIdOptions, usersKey } from './queries';
import { vipPartnerPacksOptions } from './vip-partner-queries';

export function useToggleAdminRole(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserUpdateRolesBody) =>
      api.patch(`admin/users/${userId}/admin-roles`, { json: body }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
      qc.invalidateQueries({ queryKey: [...usersKey, 'city-moderators'] });
    },
  });
}

export function useSetCrmManagerPartnerShare(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserCrmManagerPartnerShareBody) =>
      api.patch(`admin/users/${userId}/crm-manager-partner-share`, { json: body }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Ручная выдача/отзыв VIP-статуса (`granted` + необязательный срок). */
export function useSetVip(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserVipBody) =>
      api.patch(`admin/users/${userId}/vip`, { json: body }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Ручная выдача/снятие инвестора. */
export function useSetInvestor(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserInvestorBody) =>
      api.patch(`admin/users/${userId}/investor`, { json: body }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Подписка админа на дайджест очереди импорта (флаг notifyChannelImport). */
export function useToggleNotifyChannelImport(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      api.patch(`admin/users/${userId}/notify-channel-import`, { json: { value } }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Подписка админа на уведомления о новых фото знакомств на модерации. */
export function useToggleNotifyPhotoModeration(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      api.patch(`admin/users/${userId}/notify-photo-moderation`, { json: { value } }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Подписка админа на уведомления о новых задачах продвижения событий. */
export function useToggleNotifyPromotionTasks(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      api.patch(`admin/users/${userId}/notify-promotion-tasks`, { json: { value } }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Подписка SUPERADMIN на заявки с витрины пакетов. */
export function useToggleNotifyMembershipLeads(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      api.patch(`admin/users/${userId}/notify-membership-leads`, { json: { value } }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

/** Подписка админа на алерты о деградации каналов-источников. */
export function useToggleNotifyChannelHealth(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      api.patch(`admin/users/${userId}/notify-channel-health`, { json: { value } }).json<AdminUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(userByIdOptions(userId).queryKey, data);
      qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}

export function useCreateFake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FakeUserCreateInput) =>
      api.post('admin/fake-users', { json: input }).json<FakeUserDto>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fakesKey });
    },
  });
}

export function useUpdateFake(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FakeUserUpdateInput) =>
      api.patch(`admin/fake-users/${id}`, { json: input }).json<FakeUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(fakeByIdOptions(id).queryKey, data);
      qc.invalidateQueries({ queryKey: fakesKey });
    },
  });
}

export function useDeleteFake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/fake-users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fakesKey });
    },
  });
}

export function useUploadFakePhoto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`admin/fake-users/${id}/photo`, { body: formData }).json<FakeUserDto>();
    },
    onSuccess: (data) => {
      qc.setQueryData(fakeByIdOptions(id).queryKey, data);
      qc.invalidateQueries({ queryKey: fakesKey });
    },
  });
}

export function useDeleteFakePhoto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`admin/fake-users/${id}/photo`).json<FakeUserDto>(),
    onSuccess: (data) => {
      qc.setQueryData(fakeByIdOptions(id).queryKey, data);
      qc.invalidateQueries({ queryKey: fakesKey });
    },
  });
}

/** Завести пакет партнёрства по факту офлайн-оплаты. */
export function useCreateVipPartnerPack(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminCreateVipPartnerPackInput) =>
      api.post(`admin/users/${userId}/vip-partner-pack`, { json: body }).json<AdminVipPartnerPackDto[]>(),
    onSuccess: (data) => {
      qc.setQueryData(vipPartnerPacksOptions(userId).queryKey, data);
    },
  });
}

/** Отозвать ошибочную выдачу: слот возвращается, VIP гасится, если он от этой выдачи. */
export function useRevokeVipPartnerGrant(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => api.delete(`admin/vip-partner-grants/${grantId}`).json<unknown>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vipPartnerPacksOptions(userId).queryKey });
      // Статус получателя мог погаснуть — реестр юзеров показывает VIP колонкой.
      void qc.invalidateQueries({ queryKey: [...usersKey, 'list'] });
    },
  });
}
